import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  styled
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import XIcon from '@mui/icons-material/X'; // Twitter "X"
import FacebookIcon from '@mui/icons-material/Facebook';
import { useTheme } from '@mui/system';

interface ShareURLProps {
  url: string;
}

const CopiedMessage = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '-25px',
  right: 0,
  color: theme.palette.success.main,
  fontWeight: 500,
  animation: 'moveUp 0.3s ease-out forwards',
  '@keyframes moveUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(5px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const ShareURLComponent: React.FC<ShareURLProps> = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Opens a new tab for share links
  const openShareWindow = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const shareOnFacebook = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}`;
    openShareWindow(shareUrl);
  };

  const shareOnX = () => {
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      url
    )}&text=${encodeURIComponent('')}`;
    openShareWindow(shareUrl);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      timerRef.current = window.setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <Box sx={{ width: '100%', margin: '0 auto', mb: 3 }}>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{ mb: 1, color: 'text.primary' }}
      >
        Share
      </Typography>

      {/*
        For extra-small screens, we stack in a column: 
          - Row 1: icons side by side, each 50%
          - Row 2: URL field
        From small screens (sm) upward, we go side by side in one row.
      */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1
        }}
      >
        {/* Icons row */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            // On xs: full width, each icon is 50% -> flex:1
            // On sm+: auto
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <IconButton
            color="primary"
            aria-label="Share on Facebook"
            onClick={shareOnFacebook}
            // Each icon takes 50% in xs
            sx={{
              flex: { xs: 1, sm: 'initial' },
              borderRadius: '8px',
              color: isDarkMode ? '#bda4f4' : '#392e50'
            }}
          >
            <FacebookIcon fontSize="medium" />
          </IconButton>

          <IconButton
            color="primary"
            aria-label="Share on X"
            onClick={shareOnX}
            sx={{
              flex: { xs: 1, sm: 'initial' },
              borderRadius: '8px',
              color: isDarkMode ? '#bda4f4' : '#392e50'
            }}
          >
            <XIcon fontSize="medium" />
          </IconButton>
        </Box>

        {/* URL + Copy Button */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '4px 8px',
            borderRadius: 9999,
            flexGrow: 1,
            backgroundColor: 'background.paper',
            // On xs, full width. On sm, auto to fit the same row
            width: { xs: '100%', sm: 'auto' },
            // Make sure it's a single line with ellipsis
            overflow: 'hidden'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              color: 'text.primary',
              mx: 1
            }}
          >
            {url}
          </Typography>

          <Tooltip title="Copy link" arrow>
            <IconButton onClick={copyToClipboard} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Fade in={copied} unmountOnExit mountOnEnter timeout={300}>
            <CopiedMessage variant="caption">Copied!</CopiedMessage>
          </Fade>
        </Paper>
      </Box>
    </Box>
  );
};

export default ShareURLComponent;
